import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Alert,
  Group,
  NumberInput,
  Radio,
  Select,
  Textarea,
  Anchor,
  Divider,
  HoverCard,
  ActionIcon,
} from "@mantine/core";
import { notifySuccess } from "../../utils/notify";
import {
  IconArrowBackUp,
  IconAlertCircle,
  IconCurrencyDollar,
  IconInfoCircle,
} from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useResponsive } from "../../context/ResponsiveContext";
import { Payment } from "../../types/transactions";
import { RefundReason } from "../../types/payments";
import { formatCurrency } from "../../utils/cashDrawerUtils";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";

interface RefundPaymentModalProps {
  opened: boolean;
  onClose: () => void;
  /** The specific payment being refunded. */
  payment: Payment;
  onSuccess: () => void | Promise<void>;
}

type AmountType = "full" | "custom";

const REASON_OPTIONS: { value: RefundReason; label: string }[] = [
  { value: "requested_by_customer", label: "Requested by customer" },
  { value: "duplicate", label: "Duplicate" },
  { value: "fraudulent", label: "Fraudulent" },
  { value: "other", label: "Other" },
];

const STRIPE_FEES_HELP_URL =
  "https://support.stripe.com/questions/understanding-fees-for-refunded-payments";

/** Card/Stripe payments are refunded through Stripe; everything else is
 *  recorded only. Mirrors the backend's __is_stripe_payment check. */
function isStripePayment(payment: Payment): boolean {
  return (payment.method || "").startsWith("Stripe");
}

export default function RefundPaymentModal({
  opened,
  onClose,
  payment,
  onSuccess,
}: RefundPaymentModalProps) {
  const { isMobile } = useResponsive();
  const { refundPayment } = useApp();

  const alreadyRefunded = payment.refunded_amount || 0;
  const refundable = Math.max(
    0,
    Math.round((payment.amount - alreadyRefunded) * 100) / 100,
  );
  const isCard = isStripePayment(payment);

  const [amountType, setAmountType] = useState<AmountType>("full");
  const [customAmount, setCustomAmount] = useState<number>(refundable);
  const [reason, setReason] = useState<RefundReason | null>(null);
  const [note, setNote] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setAmountType("full");
      setCustomAmount(refundable);
      setReason(null);
      setNote("");
      setError(null);
      setIsProcessing(false);
    }
  }, [opened, refundable]);

  const refundAmount = amountType === "full" ? refundable : customAmount;
  const trimmedNote = note.trim();
  const isValidAmount = refundAmount > 0 && refundAmount <= refundable;
  const canSubmit =
    isValidAmount && reason !== null && trimmedNote.length > 0 && !isProcessing;

  const handleRefund = async () => {
    if (!canSubmit || reason === null) return;
    setIsProcessing(true);
    setError(null);
    try {
      await refundPayment({
        paymentId: payment.id,
        amountCents: Math.round(refundAmount * 100),
        reason,
        note: trimmedNote,
      });
      notifySuccess(`${formatCurrency(refundAmount)} refunded.`, {
        title: "Refund issued",
      });
      try {
        await Promise.resolve(onSuccess());
      } catch (e) {
        console.error("Error in refund success handler:", e);
      }
      onClose();
    } catch (err) {
      console.error("Error issuing refund:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to issue refund. Please try again.",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconArrowBackUp size={20} />
          <Text fw={600}>Refund Payment</Text>
        </Group>
      }
      size="md"
      fullScreen={isMobile}
      centered
      closeOnClickOutside={!isProcessing}
      closeOnEscape={!isProcessing}
    >
      <BrandedLoadingOverlay visible={isProcessing} />

      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        <Radio.Group
          value={amountType}
          onChange={(value) => setAmountType(value as AmountType)}
        >
          <Stack gap="xs">
            <Radio
              value="full"
              label={`Full refundable amount (${formatCurrency(refundable)})`}
              color="var(--brand-teal)"
            />
            <Radio
              value="custom"
              label="Custom amount"
              color="var(--brand-teal)"
            />
            {amountType === "custom" && (
              <Group gap="xs" wrap="nowrap" ml="xl">
                <NumberInput
                  aria-label="Custom refund amount"
                  value={customAmount}
                  onChange={(val) => setCustomAmount(Number(val) || 0)}
                  min={0.01}
                  max={refundable}
                  step={0.01}
                  decimalScale={2}
                  fixedDecimalScale
                  w={160}
                  leftSection={<IconCurrencyDollar size={14} />}
                />
                <Text size="xs" c="dimmed">
                  Max ({formatCurrency(refundable)})
                </Text>
              </Group>
            )}
          </Stack>
        </Radio.Group>

        <Select
          label="Reason"
          placeholder="Select a reason"
          data={REASON_OPTIONS}
          value={reason}
          onChange={(value) => setReason(value as RefundReason | null)}
          required
        />

        <Textarea
          label="Note"
          description="Required — why is this refund being issued?"
          placeholder="e.g. Customer cancelled, charged in error…"
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          minRows={2}
          autosize
          required
          error={
            note.length > 0 && trimmedNote.length === 0
              ? "A note is required"
              : undefined
          }
        />

        <Divider />

        <Group justify="space-between">
          <Group gap={6} wrap="nowrap">
            <Text size="sm" fw={500}>
              Amount to refund:
            </Text>
            {isCard && (
              <HoverCard
                width={300}
                shadow="md"
                withArrow
                position="top"
                openDelay={0}
                closeDelay={120}
              >
                <HoverCard.Target>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label="Refund fee details"
                  >
                    <IconInfoCircle size={16} />
                  </ActionIcon>
                </HoverCard.Target>
                <HoverCard.Dropdown>
                  <Text size="sm">
                    Refunds take 5&ndash;10 days to appear on a customer&apos;s
                    statement. Fees for the original payment won&apos;t be
                    returned, but there are no additional fees for the refund.{" "}
                    <Anchor
                      href={STRIPE_FEES_HELP_URL}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Learn more
                    </Anchor>
                  </Text>
                </HoverCard.Dropdown>
              </HoverCard>
            )}
          </Group>
          <Text size="lg" fw={700} c="red">
            {formatCurrency(isValidAmount ? refundAmount : 0)}
          </Text>
        </Group>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            color="var(--brand-teal)"
            onClick={handleRefund}
            loading={isProcessing}
            disabled={!canSubmit}
            leftSection={<IconArrowBackUp size={18} />}
          >
            Issue Refund
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
